/**
 * The starred block of the rail, then the fixed All-areas row.
 *
 * In a browser tab the rows are the user's stars in their sections, editable
 * (`SectionedRail`: drag, right-click menus, ＋ New section). Inside an
 * installed app the rows are that app's `APP_NAV` membership (`useRail`),
 * fixed — no sections, no drag, no menu — and All areas, which points outside
 * every area app, is omitted. The profile menu's "Open Conway" stays the one
 * way out.
 */
import React from 'react';

import { useInstalledApp } from '@shared/app-identity/useInstalledApp';
import { destinationById, type Destination } from '@shared/navigation/destinations';
import { useRail } from '@shared/navigation/useRail';

import { RailRow } from './RailRow';
import SectionedRail from './SectionedRail';
import SideNavItem from './SideNavItem';

import type { NavCounts } from '@app/stores/slices/uiSlice';

interface StarredRowsProps {
  isCollapsed: boolean;
  counts: NavCounts;
  /** Called after a row navigates, e.g. so a host can close itself; forwarded to every row. */
  onNavigate?: () => void;
}

const StarredRows: React.FC<StarredRowsProps> = ({ isCollapsed, counts, onNavigate }) => {
  const { ids, editable } = useRail();
  const installedApp = useInstalledApp();
  const areas = destinationById('areas');
  const rowClick = onNavigate ? { onClick: onNavigate } : {};
  const rows = ids.map(id => destinationById(id)).filter((d): d is Destination => d !== undefined);

  return (
    <>
      {editable ? (
        <SectionedRail isCollapsed={isCollapsed} counts={counts} rowClick={rowClick} />
      ) : (
        rows.map(d => (
          <RailRow
            key={d.id}
            destination={d}
            isCollapsed={isCollapsed}
            counts={counts}
            rowClick={rowClick}
          />
        ))
      )}
      {installedApp === null && areas && (
        <>
          <div className="h-3" aria-hidden="true" />
          <SideNavItem
            id={areas.id}
            label={areas.label}
            icon={areas.icon}
            isCollapsed={isCollapsed}
            path={areas.path}
            {...rowClick}
          />
        </>
      )}
    </>
  );
};

export default StarredRows;
